// frontend/admin-portal/src/pages/TreasuryAccounts.jsx
import React, { useEffect, useState, useCallback } from "react";
import {
  PageHeader,
  Card,
  Button,
  Table,
  Alert,
  Input,
  Modal,
} from "@fiberhome/shared-ui";
import { formatMoney } from "@fiberhome/shared-ui";
import { treasuryService } from "../services/treasuryService";
import { useSettings } from "../contexts/SettingsContext";

const ACCOUNT_TYPES = ["cash", "bank", "wallet", "vault"];

export default function TreasuryAccounts() {
  const { get } = useSettings();
  const currency = get("finance.currencySymbol", "IQD");

  const [accounts, setAccounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const d = await treasuryService.listAccounts();
      setAccounts(d.accounts);
      setTotal(d.totalBalance);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    setForm({
      name: "",
      type: "cash",
      currency,
      color: "#0d6efd",
      openingBalance: 0,
    });
    setModalOpen(true);
  };

  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name,
      type: a.type,
      currency: a.currency,
      color: a.color,
      bankName: a.bankName || "",
      accountNumber: a.accountNumber || "",
      iban: a.iban || "",
      isActive: a.isActive,
    });
    setModalOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await treasuryService.updateAccount(editing.id, form);
      else await treasuryService.createAccount(form);
      setModalOpen(false);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete "${a.name}"?`)) return;
    try {
      await treasuryService.removeAccount(a.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  return (
    <>
      <PageHeader
        title="Treasury Accounts"
        subtitle="Where your cash lives"
        breadcrumbs={["Home", "Treasury", "Accounts"]}
        actions={
          <Button variant="primary" onClick={openNew}>
            + New Account
          </Button>
        }
      />

      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
        </Alert>
      )}

      <Card title={`Total: ${formatMoney(total, { symbol: currency })}`}>
        <Table
          columns={[
            {
              key: "name",
              label: "Account",
              render: (r) => (
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: r.color,
                    }}
                  />
                  <strong>{r.name}</strong>
                </div>
              ),
            },
            { key: "type", label: "Type" },
            { key: "currency", label: "Currency" },
            {
              key: "currentBalance",
              label: "Balance",
              render: (r) => (
                <strong>
                  {formatMoney(r.currentBalance, { symbol: r.currency })}
                </strong>
              ),
            },
            {
              key: "isActive",
              label: "Status",
              render: (r) => (r.isActive ? "Active" : "Inactive"),
            },
            {
              key: "actions",
              label: "",
              width: 160,
              render: (r) => (
                <div className="d-flex gap-1">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(r)}
                  >
                    Edit
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(r)}>
                    Delete
                  </Button>
                </div>
              ),
            },
          ]}
          data={accounts}
          loading={loading}
          emptyMessage="No accounts"
        />
      </Card>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? `Edit: ${editing.name}` : "New Account"}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => setModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <Input
          label="Name *"
          value={form.name || ""}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
        />
        <label className="fh-input-label">Type</label>
        <select
          className="fh-input mb-3"
          value={form.type || "cash"}
          onChange={(e) => setForm({ ...form, type: e.target.value })}
        >
          {ACCOUNT_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>
        <Input
          label="Currency"
          value={form.currency || currency}
          onChange={(e) => setForm({ ...form, currency: e.target.value })}
        />
        {!editing && (
          <Input
            label="Opening Balance"
            type="number"
            value={form.openingBalance || 0}
            onChange={(e) =>
              setForm({ ...form, openingBalance: e.target.value })
            }
          />
        )}
        <Input
          label="Color"
          type="color"
          value={form.color || "#0d6efd"}
          onChange={(e) => setForm({ ...form, color: e.target.value })}
        />
      </Modal>
    </>
  );
}
